import PublicationTypes from "constants/publication-types";
import { DataTestAttributes } from "interfaces/data-test-attributes";
import PublicationRecord from "models/view-models/publication-record";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import TableOfContentsAnnexGroups from "organisms/table-of-contents/table-of-contents-annex-groups";
import React, { Fragment, useCallback } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useAdminPreview from "utilities/hooks/use-admin-preview";
import TableOfContentsCollapsePanelHeader from "organisms/table-of-contents/table-of-contents-collapse-panel-header";
import TableOfContentsCollapsePanelLink from "organisms/table-of-contents/table-of-contents-collapse-panel-link";
import StringUtils from "utilities/string-utils";
import AnnexTableOfContentsRecord from "models/view-models/table-of-contents/annex-table-of-contents-record";
import { useLocation, useParams } from "react-router-dom";
import { AnnexParams } from "interfaces/routing/route-params";
import { IconSizes } from "atoms/constants/icon-sizes";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import { useMobileTableOfContentsContext } from "./mobile-table-of-contents-context";

interface TableOfContentsAnnexProps
    extends Pick<DataTestAttributes, "dataTestId"> {
    allCollapsed: boolean;
    annexes: AnnexTableOfContentsRecord[];
    isPublic?: boolean;
    publication: PublicationRecord;
}

const TableOfContentsAnnex: React.FunctionComponent<
    TableOfContentsAnnexProps
> = (props: TableOfContentsAnnexProps) => {
    const {
        allCollapsed,
        annexes,
        dataTestId,
        isPublic = false,
        publication,
    } = props;

    const cssBaseClass = "c-table-of-contents-chapter";
    const cssPublicModifier = "-public";
    const cssEmptyModifier = "-empty";
    const cssClassName = isPublic
        ? StringUtils.joinClassNames([cssBaseClass, cssPublicModifier])
        : cssBaseClass;

    const { isAdminPreview } = useAdminPreview();
    const params = useParams<AnnexParams>();
    const location = useLocation();

    const sortedAnnexes = annexes.sort(
        (a, b) => (a.displaySequence ?? 0) - (b.displaySequence ?? 0)
    );

    const { inMobileView } = useBreakpointRanges();
    const { isBookview, onClick } = useMobileTableOfContentsContext();

    const panelTop = useCallback(
        (annex: AnnexTableOfContentsRecord) => {
            const annexPageRoute = annex.getRoute(params, isAdminPreview);

            const annexTitle =
                publication?.type === PublicationTypes.NFC
                    ? annex.getDisplayTitle(publication.type)
                    : publication?.getAnnexesDisplayLabel();

            return (
                <TableOfContentsCollapsePanelHeader
                    dataTestId={dataTestId}
                    displayTitle={annexTitle}
                    isPublic={isPublic}
                    to={annexPageRoute}
                    onClick={onClick}
                />
            );
        },
        [dataTestId, isAdminPreview, isPublic, onClick, params, publication]
    );

    if (publication.type !== PublicationTypes.NFC) {
        const firstAnnex = CollectionUtils.hasValues(sortedAnnexes)
            ? sortedAnnexes![0]
            : null;

        if (firstAnnex == null) {
            return null;
        }

        const shouldExpand =
            isBookview &&
            sortedAnnexes.some((annex) =>
                isActiveAnnex(location.pathname, annex.nfpaLabel ?? "")
            );

        const isEmpty = CollectionUtils.isEmpty(sortedAnnexes);
        const className = StringUtils.joinClassNames([
            cssClassName,
            isEmpty ? cssEmptyModifier : "",
            shouldExpand ? "-active" : "",
        ]);

        return (
            <div className={className}>
                <CollapsePanel
                    collapse={allCollapsed && !shouldExpand}
                    modifierCSSClassName="-panel"
                    buttonAriaText={publication.getAnnexesDisplayLabel()}
                    panelTop={panelTop(firstAnnex)}
                    iconSize={inMobileView ? IconSizes.Base : undefined}>
                    <nav className="c-table-of-contents-section">
                        <ul>
                            {sortedAnnexes.map((annex) => (
                                <TableOfContentsCollapsePanelLink
                                    key={annex.id}
                                    index={annex.id}
                                    isPublic={isPublic}
                                    to={annex.getRoute(params, isAdminPreview)}
                                    onClick={onClick}
                                    isActive={isActiveAnnex(
                                        location.pathname,
                                        annex.nfpaLabel ?? ""
                                    )}>
                                    {annex.getDisplayTitle()}
                                </TableOfContentsCollapsePanelLink>
                            ))}
                        </ul>
                    </nav>
                </CollapsePanel>
            </div>
        );
    }

    return (
        <Fragment>
            {sortedAnnexes.map((annex) => {
                const hasGroup = CollectionUtils.hasValues(annex.annexGroups);

                const isEmptyAndPublicModifier = !hasGroup
                    ? cssEmptyModifier
                    : "";

                const shouldExpand = isActiveAnnex(
                    location.pathname,
                    annex.nfpaLabel ?? ""
                );

                const className = StringUtils.joinClassNames([
                    cssClassName,
                    isEmptyAndPublicModifier,
                    shouldExpand ? "-active" : "",
                ]);

                return (
                    <div className={className} key={annex.id}>
                        <CollapsePanel
                            buttonAriaText={annex.getDisplayTitle(
                                publication.type
                            )}
                            collapse={allCollapsed && !shouldExpand}
                            modifierCSSClassName="-panel"
                            data-test-id="chapter-collapse-panel"
                            panelTop={panelTop(annex)}
                            iconSize={inMobileView ? IconSizes.Base : undefined}
                            scrollToWhenActive={true}>
                            {hasGroup && (
                                <TableOfContentsAnnexGroups
                                    annexGroups={annex.annexGroups}
                                    annexNfpaLabel={annex.nfpaLabel ?? ""}
                                    isPublic={isPublic}
                                />
                            )}
                        </CollapsePanel>
                    </div>
                );
            })}
        </Fragment>
    );
};

export default TableOfContentsAnnex;

const isActiveAnnex = (path: string, nfpaLabel: string): boolean =>
    path.toLowerCase().includes(`annexes/${nfpaLabel.toLowerCase()}`);
